import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContext } from "../../App";
import useCalendarViewDate from "../../hooks/useCalendarViewDate";
import { useTelawah } from "../../hooks/useTelawah";
import CalendarScheduleSlotModel from "../../models/CalendarScheduleSlotModel";
import CalendarScheduleModel from "../../models/CalendarScheduleModel";
import ApiDateParser from "../../helpers/parsers/apiDateParser";
import { Hash } from "../../api/@types/apiTypes";
import { makeApiRequest } from "../../helpers";
import CalendarScheduleCandidate from "../../api/models/CalendarScheduleCandidate";
import { ApiResponse } from "../../api/apiResponse";
import { SlotStates } from "../../api/models/ScheduleSlotModel";
import DropDown from "../../components/forms/dropdown/DropDown";
import ScheduleCalendar from "../../components/calendar/ScheduleCalendar";
import AppButton from "../../components/buttons/AppButton";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import {
  createSchedulesStudent,
  indexNewStudentAvailableSchedules,
} from "../../api/endpoints/students/schedules";
import TutorModel from "../../api/models/TutorModel";
import AvailableTutors from "../../components/calendar/AvailableTutors";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import TimeZoneAlert from "../../components/alerts/timeZone";

export default function StudentProposedSchedule() {
  const LocalBasePath = "page.candidate.schedule";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const authContext = useAuth();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { telawahOptions = [] } = useTelawah();

  const [selectedTelawahId, setSelectedTelawah] = useState<
    number | string | null | undefined
  >(authContext?.currentUser?.telawah_id);
  const { viewingDate, onChangeViewingDate } = useCalendarViewDate();
  const [schedule, setSchedule] = useState<CalendarScheduleModel | null>(null);
  const [tutors, setTutors] = useState<TutorModel[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<
    CalendarScheduleSlotModel[]
  >([]);
  const location = useLocation();
  const [selected, setSelected] = useState<string | number>("");
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    setSchedule(null);
    if (!!selectedTelawahId) {
      fetchRecitationSlots(selectedSlots, generateApiData(selectedSlots));
    }
  }, [selectedTelawahId, selected]);

  useEffect(() => {
    fetchRecitationSlots(selectedSlots, generateApiData(selectedSlots));
  }, [selectedSlots]);

  const generateApiData = (slots: CalendarScheduleSlotModel[]) => {
    if (!slots.length) return;
    let data: Hash<any> = {};
    data["day_time"] = {};
    slots.forEach((slot: CalendarScheduleSlotModel) => {
      if (!data["day_time"][slot.dayOfWeekName])
        data["day_time"][slot.dayOfWeekName] = [];
      const time = ApiDateParser.toNormalizedApiDate(slot.startTime);
      data["day_time"][slot.dayOfWeekName].push(time);
    });
    return data;
  };

  const fetchRecitationSlots = async (
    selectedSlots: CalendarScheduleSlotModel[],
    slotData?: object
  ) => {
    setIsLoading(true);
    await makeApiRequest(
      () =>
        indexNewStudentAvailableSchedules(
          selectedTelawahId!,
          slotData ?? {},
          selected ?? ""
        ),
      (res: ApiResponse) => {
        let schedule = new CalendarScheduleModel(
          res.data?.slots,
          SlotStates.available,
          authContext?.currentUser
        );
        if (res.data?.tutors.length > 0) {
          const newTutorsAvailable = res.data?.tutors.map(
            (tutor: TutorModel) => {
              const available = new TutorModel(tutor);
              return available;
            }
          );
          setTutors(newTutorsAvailable);
        }

        selectedSlots.forEach((slot) => {
          schedule.modifySlotsStateByCalendarId(
            slot.calendarId,
            SlotStates.booked
          );
        });
        setSchedule(schedule);
        setIsLoading(false);
      }
    );
  };

  const onSlotClick = (slot: CalendarScheduleSlotModel) => {
    if (slot.state === SlotStates.available) {
      setSelectedSlots([...selectedSlots, slot]);
    } else if (slot.state === SlotStates.booked) {
      setSelectedSlots(
        selectedSlots.filter((s) => s.calendarId !== slot.calendarId)
      );
    }
  };

  const resetCalendar = () => {
    setSelectedTelawah(authContext?.currentUser?.telawah_id);
    setSelectedSlots([]);
    setSchedule(null);
    setSelected("");
    fetchRecitationSlots([]);
  };

  const handleSubmit = async () => {
    const newSlots: any = [];
    selectedSlots.forEach((slot) => {
      newSlots.push(new CalendarScheduleCandidate(slot));
    });
    const body: any = {
      schedule_slots_attributes: newSlots,
    };
    if (!!selected) {
      body["tutor_id"] = selected;
    }
    const response: ApiResponse = await createSchedulesStudent({
      ...body,
    });
    if (response.isSuccess) {
      setIsDone(true);
    } else {
      response.errorMessages.forEach((errorMessage) => {
        toast?.error(errorMessage);
      });
      setIsDone(false);
    }
  };

  const handleCloseModal = () => {
    setIsDone(false);
    navigate("/student/dashboard/room");
  };
  return (
    <div className="flex flex-col w-screen h-screen overflow-y-auto divide-y divide-gray-200">
      <div className="flex flex-col justify-between gap-5 p-6 pb-4">
        <div className="flex items-center justify-between w-full">
          <h2 className="text-lg font-semibold leading-8 text-gray-900">
            {t("title")}
          </h2>

          {location.pathname !== "/student/dashboard/payment" && (
            <ArrowLeftIcon
              className="w-6 h-6 cursor-pointer ltr:rotate-180"
              onClick={() => navigate("/")}
            />
          )}
        </div>
        <div className="my-0.5">
          <TimeZoneAlert />
        </div>
        <DropDown
          name={t("chooseTelawah")}
          setValue={(val: any) => setSelectedTelawah(val)}
          defaultValue={`${selectedTelawahId}`}
          label={t("telawah")}
          placeholder={t("chooseTelawah")}
          error={false}
          options={telawahOptions}
          className="w-full"
          disabled
        />
        <p className="text-xs font-medium text-gray-400">{t("disclaimer")}</p>
      </div>
      <div className="flex w-full overflow-y-auto ">
        <div className="flex-1 w-full ">
          <ScheduleCalendar
            selectedDate={viewingDate}
            viewingDate={viewingDate}
            schedule={schedule}
            onChangeViewingDate={onChangeViewingDate}
            onSlotClick={onSlotClick}
            compact={true}
            showDates={false}
            isLoading={isLoading}
            highlightSelectedDate={false}
            proposedSchedule={true}
          />
        </div>
        <div className="h-full overflow-y-auto sticky  rtl:left-0 ltr:right-0 top-0 flex flex-col w-full  max-w-[170px] md:max-w-[260px] ">
          <div className="py-2.5 px-3 sm:py-4  md:px-6  bg-white shadow  top-0  sticky">
            {t("tutorPlaceholder")}
          </div>
          <div className="w-full ">
            {tutors?.length > 0 &&
              tutors.map((tutor) => (
                <AvailableTutors
                  selected={selected}
                  tutor={tutor}
                  setSelected={setSelected}
                />
              ))}
          </div>
        </div>
      </div>
      {!!selectedSlots.length && (
        <div className="flex flex-col justify-between w-full gap-3 px-6 py-4 md:flex-row md:items-center">
          <p className="text-sm font-medium text-gray-400">
            {t("selectSpecificTutor")}
          </p>
          <div className="flex justify-end flex-shrink-0 gap-4">
            <AppButton buttonShape="secondary" onClick={resetCalendar}>
              {t("reSchedule")}
            </AppButton>
            <AppButton buttonShape="primary" onClick={handleSubmit}>
              {t("send")}
            </AppButton>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={isDone}
        btnText={"goToTheMeeting"}
        onClose={handleCloseModal}
        email={authContext?.currentUser?.email || ""}
      />
    </div>
  );
}
